import type { FolioRichContentPayload } from '@contract-analyzer/api-client';

import { parseStoredRichEvent } from '@/hooks/folio-event-decoder';
import type {
  FolioMessageEvent,
  FolioMode,
  FolioStreamEvent,
  FolioStreamMessage,
  LastRequest,
} from '@/hooks/folio-types';

export function createMessage(
  role: FolioStreamMessage['role'],
  content: string,
  mode: FolioMode | null,
  isStreaming: boolean,
  options?: {
    readonly sourceRole?: FolioStreamMessage['sourceRole'];
    readonly feedbackTargetMessageId?: string;
  },
): FolioStreamMessage {
  return {
    id: crypto.randomUUID(),
    role,
    ...(options?.sourceRole === undefined ? {} : { sourceRole: options.sourceRole }),
    ...(options?.feedbackTargetMessageId === undefined
      ? {}
      : { feedbackTargetMessageId: options.feedbackTargetMessageId }),
    content,
    events: [],
    mode,
    isStreaming,
  };
}

function updateAssistantMessage(
  messages: readonly FolioStreamMessage[],
  assistantMessageId: string,
  updater: (message: FolioStreamMessage) => FolioStreamMessage,
): readonly FolioStreamMessage[] {
  return messages.map((message) => {
    if (message.id !== assistantMessageId) {
      return message;
    }

    return updater(message);
  });
}

export function finalizeAssistantMessage(
  messages: readonly FolioStreamMessage[],
  assistantMessageId: string,
  preserveEmptyMessage: boolean = false,
): readonly FolioStreamMessage[] {
  return messages.flatMap((message) => {
    if (message.id !== assistantMessageId) {
      return [message];
    }

    const hasOnlyDoneEvents = message.events.every((event) => event.type === 'done');

    if (
      !preserveEmptyMessage &&
      message.content.length === 0 &&
      (message.events.length === 0 || hasOnlyDoneEvents)
    ) {
      return [];
    }

    return [{ ...message, isStreaming: false }];
  });
}

export function sanitizeErrorMessage(message: string): string {
  const normalizedMessage = message.trim();
  const lowercaseMessage = normalizedMessage.toLowerCase();

  if (
    lowercaseMessage.includes('permission') ||
    lowercaseMessage.includes('forbidden') ||
    lowercaseMessage.includes('not authorized') ||
    lowercaseMessage.includes('unauthorized')
  ) {
    return 'You do not have permission to do that.';
  }

  return normalizedMessage;
}

/**
 * A message's events with `event` added. Events only ever join at the end, so
 * the last event holds the highest id handed out so far, including the id of a
 * partial output that a terminal `code_execution` is about to drop.
 */
function appendEvent(
  events: readonly FolioMessageEvent[],
  event: FolioStreamEvent,
): readonly FolioMessageEvent[] {
  const tail = events.at(-1);
  const id = (tail?.id ?? -1) + 1;

  if (event.type === 'reasoning_summary' && tail?.type === 'reasoning_summary') {
    return [...events.slice(0, -1), { type: 'reasoning_summary', data: event.data, id: tail.id }];
  }
  if (event.type === 'code_partial_output') {
    // Merge adjacent partial chunks on the same channel so the UI only has to render one
    // growing stdout/stderr block per channel.
    if (tail?.type === 'code_partial_output' && tail.data.channel === event.data.channel) {
      const chunk = `${tail.data.chunk}${event.data.chunk}`;
      return [
        ...events.slice(0, -1),
        { type: 'code_partial_output', data: { channel: event.data.channel, chunk }, id: tail.id },
      ];
    }
    return [...events, { ...event, id }];
  }
  if (event.type === 'code_execution') {
    // Drop any preceding partial-output events once the terminal code_execution arrives;
    // the terminal event already carries the full stdout/stderr.
    const pruned = events.filter((existing) => existing.type !== 'code_partial_output');
    return [...pruned, { ...event, id }];
  }
  return [...events, { ...event, id }];
}

/** A saved message's events, numbered in the order the conversation stored them. */
export function storedMessageEvents(
  payloads: readonly FolioRichContentPayload[],
): readonly FolioMessageEvent[] {
  const events: FolioMessageEvent[] = [];
  for (const payload of payloads) {
    const event = parseStoredRichEvent(payload);
    if (event !== null) {
      events.push({ ...event, id: events.length });
    }
  }
  return events;
}

export function applyStreamEvent(
  messages: readonly FolioStreamMessage[],
  assistantMessageId: string,
  event: FolioStreamEvent,
  currentMode: FolioMode | null,
): readonly FolioStreamMessage[] {
  return updateAssistantMessage(messages, assistantMessageId, (message) => {
    const nextMode = event.type === 'mode_change' ? event.mode : currentMode ?? message.mode;

    return {
      ...message,
      content:
        event.type === 'text_delta' ? `${message.content}${event.content}` : message.content,
      ...(event.type === 'done' && event.assistantMessageId !== undefined
        ? { feedbackTargetMessageId: event.assistantMessageId }
        : {}),
      events:
        event.type === 'text_delta' || event.type === 'error'
          ? message.events
          : appendEvent(message.events, event),
      mode: nextMode,
      isStreaming: event.type === 'done' ? false : message.isStreaming,
    };
  });
}

export function removeMessagePair(
  messages: readonly FolioStreamMessage[],
  lastRequest: LastRequest,
): readonly FolioStreamMessage[] {
  return messages.filter(
    (message) =>
      message.id !== lastRequest.userMessageId &&
      message.id !== lastRequest.assistantMessageId,
  );
}
